<template>
  <header :class="[
    'fixed top-0 w-full z-50 transition-all duration-300', 
    scrolled ? 'py-1 backdrop-blur-md bg-dark-blue/50 shadow-md' : 'py-4'
  ]">
    <div class="container mx-auto px-4">
      <div class="flex justify-between items-center">
        <div class="logo">
          <router-link to="/" class="flex items-center">
            <img 
              src="@/assets/images/rendery.png" 
              alt="Rendery" 
              :class="[
                'transition-all duration-300',
                scrolled ? 'h-[50px]' : 'h-[78px]'
              ]" 
            />
          </router-link>
        </div>
        <div class="flex items-center gap-3">
          <!-- Language Selector -->
          <div ref="languageDropdown" class="relative z-50">
            <button 
              @click="toggleLanguageDropdown" 
              class="flex items-center justify-center rounded-full border-2 border-white/30 shadow-md transition-all hover:shadow-lg focus:outline-none transform hover:scale-105 bg-white/10 backdrop-blur-sm text-white font-bold"
              :class="[scrolled ? 'w-8 h-8 text-xs' : 'w-11 h-11 text-sm']"
            >
              {{ currentLanguage.toUpperCase() }}
            </button>
            
            <!-- Dropdown -->
            <div 
              v-show="showLanguageDropdown" 
              class="absolute right-0 mt-2 py-2 w-40 bg-white/90 backdrop-blur-md rounded-xl shadow-lg z-50 border border-purple-100"
            >
              <div class="py-1">
                <button
                  v-for="lang in languages" 
                  :key="lang.code"
                  @click="selectLanguage(lang.code)"
                  class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-purple-100/60 w-full text-left transition-colors duration-200"
                  :class="{ 'bg-purple-50/80': currentLanguage === lang.code }"
                >
                  <div 
                    class="w-7 h-7 rounded-full mr-3 bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700 shadow-sm"
                  >
                    {{ lang.code.toUpperCase() }}
                  </div>
                  <span>{{ lang.name }}</span>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Sign In Button -->
          <button 
            :class="[
              'border-2 border-white/30 text-white font-semibold rounded-full backdrop-blur-sm bg-white/10 transform transition-all duration-300 ease-in-out hover:scale-105 hover:bg-white/20 hover:border-white/50 hover:shadow-lg',
              scrolled ? 'py-1.5 px-5 text-sm' : 'py-2.5 px-7 text-base'
            ]"
            @click="openSignInModal"
          >
            Sign In
          </button>
          
          <!-- Sign Up Button -->
          <button 
            :class="[
              'bg-gradient-to-br from-green-500 via-teal-500 to-cyan-500 text-white font-bold rounded-full shadow-lg transform transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-xl',
              scrolled ? 'py-1.5 px-5 text-sm' : 'py-2.5 px-7 text-base'
            ]"
            @click="openSignUpModal"
          >
            Sign Up
          </button>
        </div>
      </div>
    </div>
  </header>
  
  <!-- Sign Up Modal -->
  <SignUpModal :show="showSignUpModal" :initial-mode="modalMode" @close="closeSignUpModal" />
</template>

<script>
import emitter from '@/eventBus';
import SignUpModal from '@/components/SignUpModal.vue';

export default {
  name: 'Navbar',
  components: {
    SignUpModal
  },
  data() {
    return {
      scrolled: false,
      showLanguageDropdown: false,
      showSignUpModal: false,
      modalMode: 'signup', // 'signup' or 'signin'
      currentLanguage: 'en',
      languages: [
        { code: 'en', name: 'English' },
        { code: 'pt', name: 'Português' },
        { code: 'es', name: 'Español' },
        { code: 'de', name: 'Deutsch' },
        { code: 'fr', name: 'Français' }
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    document.addEventListener('click', this.closeDropdownOnClickOutside);
    
    // Check for stored language preference
    const storedLang = localStorage.getItem('preferredLanguage');
    
    if (storedLang) {
      // Set the stored language immediately without any notifications
      this.currentLanguage = storedLang;
      emitter.emit('change-language', storedLang);
    } else {
      // No stored preference, detect user location silently
      this.detectUserLocationSilently();
    }
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('click', this.closeDropdownOnClickOutside);
  },
  methods: {
    handleScroll() {
      this.scrolled = window.scrollY > 50;
    },
    toggleLanguageDropdown() {
      this.showLanguageDropdown = !this.showLanguageDropdown;
    },
    closeDropdownOnClickOutside(event) {
      if (this.showLanguageDropdown && this.$refs.languageDropdown) {
        if (!this.$refs.languageDropdown.contains(event.target)) {
          this.showLanguageDropdown = false;
        }
      }
    },
    detectUserLocationSilently() {
      // Function to detect user's location and set appropriate language silently
      fetch('https://ipapi.co/json/')
        .then(response => response.json())
        .then(data => {
          // Map of country codes to language codes
          const countryToLanguage = {
            // English-speaking countries
            'US': 'en', 'GB': 'en', 'AU': 'en', 'NZ': 'en', 'IE': 'en',
            // Portuguese-speaking countries
            'BR': 'pt', 'PT': 'pt', 'AO': 'pt', 'MZ': 'pt',
            // Spanish-speaking countries
            'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'PE': 'es', 'VE': 'es', 
            'CL': 'es', 'EC': 'es', 'GT': 'es', 'CU': 'es', 'BO': 'es', 
            'DO': 'es', 'HN': 'es', 'PY': 'es', 'SV': 'es', 'NI': 'es', 'CR': 'es', 
            'PA': 'es', 'UY': 'es',
            // German-speaking countries
            'DE': 'de', 'AT': 'de', 'LI': 'de',
            // French-speaking countries
            'FR': 'fr', 'BE': 'fr', 'LU': 'fr', 
            'MC': 'fr', 'SN': 'fr', 'CI': 'fr', 'ML': 'fr', 'CM': 'fr', 
            'MG': 'fr', 'DZ': 'fr', 'TN': 'fr', 'MA': 'fr',
            // Multilingual countries - assigned based on majority or official language status
            'CA': 'en', // Canada - default to English
            'CH': 'de'  // Switzerland - default to German
          };

          let detectedLang;
          
          // Handle special cases for multilingual countries
          if (data.country_code === 'CA' && data.region_code) {
            // Quebec is primarily French-speaking
            if (data.region_code === 'QC') {
              detectedLang = 'fr';
            } else {
              detectedLang = 'en';
            }
          } else if (data.country_code === 'CH' && data.region_code) {
            // Swiss French-speaking cantons
            const frenchCantons = ['GE', 'VD', 'NE', 'JU', 'FR', 'VS'];
            if (frenchCantons.includes(data.region_code)) {
              detectedLang = 'fr';
            } else {
              detectedLang = 'de'; 
            }
          } else {
            detectedLang = countryToLanguage[data.country_code];
          }

          // If we have detected a language, set it silently
          if (detectedLang) {
            // Only set if there's no existing preference
            if (!localStorage.getItem('preferredLanguage')) {
              this.currentLanguage = detectedLang;
              
              // Store the detected language
              localStorage.setItem('preferredLanguage', detectedLang);
              
              emitter.emit('change-language', detectedLang);
              
              if (process.env.NODE_ENV === 'development') {
                console.log(`Language set to ${detectedLang} based on location: ${data.country_name}${data.region_code ? `, ${data.region_code}` : ''}`);
              }
            }
          } else {
            // Fallback to browser language
            this.detectBrowserLanguageSilently();
          }
        })
        .catch(error => {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error detecting user location:', error);
          }
          // Fallback to browser language
          this.detectBrowserLanguageSilently();
        });
    },
    detectBrowserLanguageSilently() {
      // Only try if we don't have a stored preference
      if (localStorage.getItem('preferredLanguage')) {
        return;
      }

      // Get browser language
      const browserLang = navigator.language || navigator.userLanguage;
      
      // Extract the language code (first 2 chars)
      const langCode = browserLang.substring(0, 2).toLowerCase();
      
      // Check if we support this language
      const supportedLanguages = this.languages.map(lang => lang.code);
      
      if (supportedLanguages.includes(langCode)) {
        this.currentLanguage = langCode;
        
        // Store the detected language
        localStorage.setItem('preferredLanguage', langCode);
        
        emitter.emit('change-language', langCode);
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`Language set to ${langCode} based on browser language: ${browserLang}`);
        }
      }
    },
    getLanguageName(langCode) {
      const language = this.languages.find(lang => lang.code === langCode);
      return language ? language.name : 'English';
    },
    selectLanguage(langCode) {
      this.currentLanguage = langCode;
      this.showLanguageDropdown = false;
      
      // Store the language as explicitly selected by the user
      localStorage.setItem('preferredLanguage', langCode);
      
      emitter.emit('change-language', langCode);
    },

    openSignUpModal() {
      this.modalMode = 'signup';
      this.showSignUpModal = true;
    },
    openSignInModal() {
      this.modalMode = 'signin';
      this.showSignUpModal = true;
    },
    closeSignUpModal() {
      this.showSignUpModal = false;
    }
  }
}
</script>

<style scoped>
/* Ensure no backgrounds on any navbar elements */
.logo, .logo * {
  background: transparent;
}

/* Make the logo image properly transparent */
.logo img {
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.2));
  transition: all 0.3s ease;
  max-width: 100%;
}



/* Add dark blue background color for reference */
.bg-dark-blue {
  background-color: #10103c;
}

/* Increase shadow when scrolled */
.shadow-md {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 
              0 2px 4px -1px rgba(0, 0, 0, 0.1);
}

/* Adjust transition timing */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}
</style> 